export type DisplayContext = "large" | "medium" | "small";

export type ImageSizeTier =
  | "t_thumb"
  | "t_cover_small"
  | "t_cover_big"
  | "t_cover_big_2x"
  | "t_screenshot_big"
  | "t_720p"
  | "t_1080p";

const DISPLAY_CONTEXT_MAP: Record<DisplayContext, ImageSizeTier> = {
  large: "t_cover_big_2x",
  medium: "t_cover_big",
  small: "t_thumb",
};

const VALID_TIERS: Set<string> = new Set([
  "t_thumb",
  "t_cover_small",
  "t_cover_big",
  "t_cover_big_2x",
  "t_screenshot_big",
  "t_720p",
  "t_1080p",
]);

const IGDB_CDN_BASE = "https://images.igdb.com/igdb/image/upload";

/**
 * Constructs an IGDB CDN URL for the given imageId and display context.
 * Returns undefined for falsy imageId values.
 * Defaults unknown contexts to "large" (t_cover_big_2x).
 */
export function getImageUrl(
  imageId: string | undefined | null,
  context: string
): string | undefined {
  if (!imageId) return undefined;

  const tier =
    DISPLAY_CONTEXT_MAP[context as DisplayContext] ??
    DISPLAY_CONTEXT_MAP["large"];

  return `${IGDB_CDN_BASE}/${tier}/${imageId}.jpg`;
}

/**
 * Parses an IGDB CDN URL and extracts the imageId and sizeTier.
 * Returns null for malformed URLs that don't match the expected pattern.
 */
export function parseImageUrl(
  url: string
): { imageId: string; sizeTier: ImageSizeTier } | null {
  const match = url.match(
    /^https:\/\/images\.igdb\.com\/igdb\/image\/upload\/([^/]+)\/([^/]+)\.jpg$/
  );

  if (!match) return null;

  const [, tier, imageId] = match;

  if (!VALID_TIERS.has(tier)) return null;

  return { imageId, sizeTier: tier as ImageSizeTier };
}

/**
 * Replaces the size tier segment in an existing IGDB CDN URL.
 */
export function replaceImageTier(url: string, newTier: ImageSizeTier): string {
  const tierPattern = new RegExp(
    `(${IGDB_CDN_BASE.replace(/[/.]/g, "\\$&")}/)([^/]+)(/)`
  );
  return url.replace(tierPattern, `$1${newTier}$3`);
}
